import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import Icon from "@mdi/react";
import { mdiChevronDoubleRight, mdiCashMultiple, mdiWeb, mdiFileImportOutline } from "@mdi/js";

import { useAuth } from "../contexts/AuthContext";
import StoreBanner from "../components/store-banner";
import YouTubeEmbed from "../components/YouTubeEmbed";
import "./HowItWorks.css";
import "./header.css";
import "./Common.css";

import danceLocaleIcon from "../assets/dance_locale_3-200h.png";
import { useMobileMenu } from "../hooks/useMobileMenu";

const LEARN_MORE_FAQS = [
    {
        q: "Can I use just one feature and ignore the rest?",
        a: "Of course. You can use a single feature on its own, with no need to migrate or replace the software you already rely on. Customise which features show up so your dashboard only reflects how you actually run your studio. Use one tool, all three, or anything in between.",
    },
    {
        q: "Do students have to download an app to enrol or pay?",
        a: "No. Students can enrol, pay, and book directly on the web through your branded enrolment and payment pages. The mobile app is optional and adds push notifications, offline tickets, and quick messaging, but nothing requires it.",
    },
    {
        q: "Can I use Dance Localé with my existing dance school website?",
        a: "Yes. Each school gets a public enquiry page that you link from any 'Enquire Now' button on your site, or share via Instagram, Facebook, or email. It's the same as updating any other link on your website, no plugins or developer needed.",
    },
    {
        q: "Can I customise the look of the enrolment and payment pages?",
        a: "Yes. From Settings → Branding you control header, background, text, and button colours, with a live preview. Your school name, logo, and cover photo also appear on every public page so students get a seamless experience clicking through from your site.",
    },
    {
        q: "How do I get paid, and what does it cost?",
        a: "Stripe Connect handles online payments and pays out to your bank weekly or monthly. Students pay one service fee on online payments across bookings, ticketed events, and class enrolments. It covers the 2.5% Dance Localé fee and online payment processing, and it is the same whichever online payment method they use. No cost to you, no setup fees. You can also enable bank-transfer payments on enrolments and bookings, so students can pay you directly with no online service fee.",
    },
    {
        q: "Already using spreadsheets, and want easy migration?",
        a: "Import your students in minutes via bulk upload. Existing enrolments, contact details, and class assignments come across in one go, so you don't have to start from scratch.",
    },
];

const HowItWorks = () => {
    const navigate = useNavigate();
    const { user, logout } = useAuth();
    const [openFaq, setOpenFaq] = useState(null);

    const handleLogout = () => {
        logout();
    };

    useMobileMenu();

    return (
        <div className="learn-more-page">
            {/* Header — same structure as home.js */}
            <div data-role="Header" className="home-header-container">
                <header className="home-header">
                    <a href="/" className="home-logo1">
                        <img alt="logo" src={danceLocaleIcon} className="home-image1" />
                        <span className="nav-wordmark">Dance Localé</span>
                    </a>
                    <div className="home-menu1">
                        <a href="/" className="home-text10" style={{ cursor: "pointer", textDecoration: "none" }}>
                            Home
                        </a>
                        <a
                            href="/learn-more"
                            className="home-text11"
                            style={{ cursor: "pointer", textDecoration: "none" }}>
                            Learn More
                        </a>
                        <span
                            className="home-text11"
                            style={{ color: "var(--dl-color-orange-default)", cursor: "pointer" }}
                            onClick={() => navigate("/how-it-works")}>
                            How it Works
                        </span>
                        <a
                            href="/features"
                            className="home-text11"
                            style={{ cursor: "pointer", textDecoration: "none" }}>
                            Features
                        </a>
                        {user ? (
                            <>
                                {user.organisationId && (
                                    <a
                                        href="/dashboard"
                                        className="home-text12"
                                        style={{ cursor: "pointer", textDecoration: "none" }}>
                                        Dashboard
                                    </a>
                                )}
                                <a
                                    href="/my-hub"
                                    className="home-text12"
                                    style={{ cursor: "pointer", textDecoration: "none" }}>
                                    My Hub
                                </a>
                                <button
                                    onClick={handleLogout}
                                    className="home-text13"
                                    style={{ cursor: "pointer", background: "none", border: "none", color: "inherit" }}>
                                    Logout
                                </button>
                            </>
                        ) : (
                            <>
                                <a
                                    href="/login"
                                    className="home-text12"
                                    style={{ cursor: "pointer", textDecoration: "none" }}>
                                    Login
                                </a>
                                <a
                                    href="/contact"
                                    className="home-text13"
                                    style={{ cursor: "pointer", textDecoration: "none" }}>
                                    Contact
                                </a>
                            </>
                        )}
                    </div>
                    <div className="home-container11">
                        <StoreBanner />
                    </div>
                    <div data-role="BurgerMenu" className="home-burger-menu">
                        <svg viewBox="0 0 1024 1024" className="home-menu2">
                            <path d="M810.667 725.333h-597.333c-47.061 0-85.333 38.272-85.333 85.333s38.272 85.333 85.333 85.333h597.333c47.061 0 85.333-38.272 85.333-85.333s-38.272-85.333-85.333-85.333z"></path>
                            <path d="M810.667 426.667h-597.333c-47.061 0-85.333 38.272-85.333 85.333s38.272 85.333 85.333 85.333h597.333c47.061 0 85.333-38.272 85.333-85.333s-38.272-85.333-85.333-85.333z"></path>
                            <path d="M810.667 128h-597.333c-47.061 0-85.333 38.272-85.333 85.333s38.272 85.333 85.333 85.333h597.333c47.061 0 85.333-38.272 85.333-85.333s-38.272-85.333-85.333-85.333z"></path>
                        </svg>
                    </div>
                </header>

                {/* Mobile Menu */}
                <div data-role="MobileMenu" className="home-mobile-menu">
                    <div className="home-top">
                        <div className="home-logo2">
                            <img alt="logo" src={danceLocaleIcon} className="home-image2" />
                        </div>
                        <div data-role="CloseMobileMenu" className="home-close-menu">
                            <svg viewBox="0 0 1024 1024" className="home-icon15">
                                <path d="M810 274l-238 238 238 238-60 60-238-238-238 238-60-60 238-238-238-238 60-60 238 238 238-238z"></path>
                            </svg>
                        </div>
                    </div>
                    <div className="home-mid">
                        <a href="/" className="home-text14" style={{ cursor: "pointer", textDecoration: "none" }}>
                            Home
                        </a>
                        <a
                            href="/learn-more"
                            className="home-text15"
                            style={{ cursor: "pointer", textDecoration: "none" }}>
                            Learn More
                        </a>
                        <span
                            className="home-text15"
                            onClick={() => navigate("/how-it-works")}
                            style={{ cursor: "pointer", color: "var(--dl-color-orange-default)" }}>
                            How it Works
                        </span>
                        <a
                            href="/features"
                            className="home-text15"
                            style={{ cursor: "pointer", textDecoration: "none" }}>
                            Features
                        </a>
                        {user ? (
                            <>
                                {user.organisationId && (
                                    <a
                                        href="/dashboard"
                                        className="home-text16"
                                        style={{ cursor: "pointer", textDecoration: "none" }}>
                                        Dashboard
                                    </a>
                                )}
                                <a
                                    href="/my-hub"
                                    className="home-text16"
                                    style={{ cursor: "pointer", textDecoration: "none" }}>
                                    My Hub
                                </a>
                                <span className="home-text17" onClick={handleLogout} style={{ cursor: "pointer" }}>
                                    Logout
                                </span>
                            </>
                        ) : (
                            <>
                                <a
                                    href="/login"
                                    className="home-text16"
                                    style={{ cursor: "pointer", textDecoration: "none" }}>
                                    Login
                                </a>
                                <a
                                    href="/contact"
                                    className="home-text17"
                                    style={{ cursor: "pointer", textDecoration: "none" }}>
                                    Contact
                                </a>
                            </>
                        )}
                    </div>
                </div>
            </div>

            {/* Hero Section */}
            <div className="learn-more-hero content-card">
                <h1 className="learn-more-headline">
                    If you've juggled spreadsheets, DMs, and a separate ticketing site, this page is for you.
                </h1>
                <p className="learn-more-subtext">Three short ads about why. Full walkthroughs about how.</p>
                <p className="learn-more-support"><em>Use one tool, all three, or anything in between.</em></p>
                <div className="learn-more-hero-cta">
                    <a href="/register" className="home-cta-primary">
                        Register Free
                    </a>
                    <a href="/learn-more" className="learn-more-pricing-link">
                        Back to Learn More <Icon path={mdiChevronDoubleRight} size={0.8} />
                    </a>
                </div>
                {/* <span className="learn-more-scroll-hint">See what it does below</span> */}
            </div>

            {/* Video Container Card
                To add a video: replace videoId={null} with the YouTube video ID (the part after v= in a YouTube URL).
                See docs/WEB-COMPONENT-GUIDE.md → YouTubeEmbed for full instructions. */}
            <div className="learn-more-video-container content-card" id="videos">
                {/* Row 1: Marketing Ads */}
                <div className="learn-more-video-row">
                    <h2 className="learn-more-row-label learn-more-row-label--tight">See what Dance Localé can do</h2>
                    <p className="learn-more-row-subline home-hero-support">
                        <em>Three jobs most studios run on three different tools: classes, privates, and events.</em>
                    </p>
                    <div className="learn-more-video-grid">
                        {/* School Management Ad */}
                        <div className="learn-more-video-cell">
                            <p className="learn-more-video-copy">Manually tracking class attendance on Excel?</p>
                            <YouTubeEmbed videoId="bQEDtz5qpf8" title="School Management" />
                            <a href="/register" className="learn-more-video-cta">
                                Set Up Your School Free <Icon path={mdiChevronDoubleRight} size={0.6} />
                            </a>
                        </div>

                        {/* Booking Ad */}
                        <div className="learn-more-video-cell">
                            <p className="learn-more-video-copy">Chasing students on Instagram for private slots?</p>
                            <YouTubeEmbed videoId="I6jFNBmP9a8" title="Private Lesson Booking" />
                            <a href="/register" className="learn-more-video-cta">
                                Start Teaching Free <Icon path={mdiChevronDoubleRight} size={0.6} />
                            </a>
                        </div>

                        {/* Ticketing Ad */}
                        <div className="learn-more-video-cell">
                            <p className="learn-more-video-copy">
                                Using a separate platform to sell social and congress tickets?
                            </p>
                            <YouTubeEmbed videoId="2Z_5wAVa1Gk" title="Event Ticketing" />
                            <a href="/register" className="learn-more-video-cta">
                                List Your Event Free <Icon path={mdiChevronDoubleRight} size={0.6} />
                            </a>
                        </div>
                    </div>
                </div>

                <hr className="learn-more-divider" />

                <h2 className="learn-more-row-label learn-more-row-label--tight">Fits how you already work</h2>
                <div className="learn-more-feature-list-link">
                    <a href="/features" className="learn-more-pillar-link">
                        See feature list <Icon path={mdiChevronDoubleRight} size={0.7} />
                    </a>
                </div>

                <div className="learn-more-pillars">
                    <div className="learn-more-pillar">
                        <div className="learn-more-pillar-icon">
                            <Icon path={mdiWeb} size={1.4} />
                        </div>
                        <h3 className="learn-more-pillar-title">Integrates with your website</h3>
                        <p className="learn-more-pillar-body">
                            Link buttons on your site straight to branded enquiry and payment pages. Your colours, your
                            logo, your school name throughout.
                        </p>
                    </div>

                    <div className="learn-more-pillar">
                        <div className="learn-more-pillar-icon">
                            <Icon path={mdiCashMultiple} size={1.4} />
                        </div>
                        <h3 className="learn-more-pillar-title">Free for you, fair for students</h3>
                        <p className="learn-more-pillar-body">
                            Free to use, no setup fees. Students have the option to pay via Stripe and Apple Pay for
                            convenience, or bank-transfer payments.
                        </p>
                    </div>

                    <div className="learn-more-pillar">
                        <div className="learn-more-pillar-icon">
                            <Icon path={mdiFileImportOutline} size={1.4} />
                        </div>
                        <h3 className="learn-more-pillar-title">Already on spreadsheets?</h3>
                        <p className="learn-more-pillar-body">
                            Import your students in minutes. Existing enrolments, contact details, and class assignments
                            come across in one go.
                        </p>
                    </div>
                </div>

                <hr className="learn-more-divider" />

                {/* Row 2: Product Tours */}
                <div className="learn-more-video-row">
                    <h2 className="learn-more-row-label">See it in action – full walkthroughs</h2>
                    <div className="learn-more-video-grid">
                        <div className="learn-more-video-cell">
                            <h3 className="learn-more-tour-title">Manage your dance school</h3>
                            <YouTubeEmbed videoId="XP6joOGngg8" title="Tour: School Management" />
                        </div>
                        <div className="learn-more-video-cell">
                            <h3 className="learn-more-tour-title">Manage private lesson bookings</h3>
                            <YouTubeEmbed videoId={null} title="Tour: Private Lesson Bookings" />
                        </div>
                        <div className="learn-more-video-cell">
                            <h3 className="learn-more-tour-title">Create event tickets</h3>
                            <YouTubeEmbed videoId={null} title="Tour: Event Tickets" />
                        </div>
                    </div>
                </div>
            </div>

            {/* Common Questions — objection handling before final CTA */}
            <div className="learn-more-faq content-card">
                <h2 className="learn-more-faq-heading">Common questions</h2>
                <p className="learn-more-faq-subhead">
                    The things dance schools most often ask before getting started.
                </p>

                <div className="learn-more-faq-list">
                    {LEARN_MORE_FAQS.map((item, index) => {
                        const isOpen = openFaq === index;
                        return (
                            <div key={index} className="learn-more-faq-item">
                                <button
                                    type="button"
                                    className="learn-more-faq-question"
                                    aria-expanded={isOpen}
                                    onClick={() => setOpenFaq(isOpen ? null : index)}>
                                    <span>{item.q}</span>
                                    <span className="learn-more-faq-toggle">{isOpen ? "−" : "+"}</span>
                                </button>
                                {isOpen && <p className="learn-more-faq-answer">{item.a}</p>}
                            </div>
                        );
                    })}
                </div>

                <div className="learn-more-faq-footer">
                    <a href="/faq" className="learn-more-faq-link">
                        See all FAQs <Icon path={mdiChevronDoubleRight} size={0.6} />
                    </a>
                </div>
            </div>

            {/* Final CTA Section */}
            <div className="learn-more-final-cta">
                <h2 className="learn-more-final-heading">Register free today</h2>
                <a href="/register" className="home-cta-primary">
                    Register Free
                </a>
                <div className="learn-more-store-banner">
                    <StoreBanner />
                </div>
            </div>

            {/* Footer — same structure as home.js */}
            <div className="home-footer">
                <footer className="home-container22">
                    <div className="home-container23">
                        <span
                            className="home-text56"
                            onClick={() => navigate("/privacy-policy")}
                            style={{ cursor: "pointer" }}>
                            Privacy policy
                        </span>
                        <span
                            className="home-text57"
                            onClick={() => navigate("/terms-conditions")}
                            style={{ cursor: "pointer" }}>
                            Terms of Service
                        </span>
                        <span
                            className="home-text58"
                            onClick={() => navigate("/contact")}
                            style={{ cursor: "pointer" }}>
                            Contact
                        </span>
                    </div>
                    <div className="home-divider"></div>
                    <div className="home-container24">
                        <span className="home-text59 Body2">
                            © {new Date().getFullYear()} Dance Localé. All rights reserved
                        </span>
                        <div className="home-container25">
                            <a
                                href="https://www.instagram.com/dancelocalehq/"
                                target="_blank"
                                rel="noreferrer noopener"
                                className="home-link5">
                                <div className="home-container26">
                                    <svg viewBox="0 0 877.7142857142857 1024" className="home-icon25">
                                        <path d="M585.143 512c0-80.571-65.714-146.286-146.286-146.286s-146.286 65.714-146.286 146.286 65.714 146.286 146.286 146.286 146.286-65.714 146.286-146.286zM664 512c0 124.571-100.571 225.143-225.143 225.143s-225.143-100.571-225.143-225.143 100.571-225.143 225.143-225.143 225.143 100.571 225.143 225.143zM725.714 277.714c0 29.143-23.429 52.571-52.571 52.571s-52.571-23.429-52.571-52.571 23.429-52.571 52.571-52.571 52.571 23.429 52.571 52.571zM438.857 152c-64 0-201.143-5.143-258.857 17.714-20 8-34.857 17.714-50.286 33.143s-25.143 30.286-33.143 50.286c-22.857 57.714-17.714 194.857-17.714 258.857s-5.143 201.143 17.714 258.857c8 20 17.714 34.857 33.143 50.286s30.286 25.143 50.286 33.143c57.714 22.857 194.857 17.714 258.857 17.714s201.143 5.143 258.857-17.714c20-8 34.857-17.714 50.286-33.143s25.143-30.286 33.143-50.286c22.857-57.714 17.714-194.857 17.714-258.857s5.143-201.143-17.714-258.857c-8-20-17.714-34.857-33.143-50.286s-30.286-25.143-50.286-33.143c-57.714-22.857-194.857-17.714-258.857-17.714zM877.714 512c0 60.571 0.571 120.571-2.857 181.143-3.429 70.286-19.429 132.571-70.857 184s-113.714 67.429-184 70.857c-60.571 3.429-120.571 2.857-181.143 2.857s-120.571 0.571-181.143-2.857c-70.286-3.429-132.571-19.429-184-70.857s-67.429-113.714-70.857-184c-3.429-60.571-2.857-120.571-2.857-181.143s-0.571-120.571 2.857-181.143c3.429-70.286 19.429-132.571 70.857-184s113.714-67.429 184-70.857c60.571-3.429 120.571-2.857 181.143-2.857s120.571-0.571 181.143 2.857c70.286 3.429 132.571 19.429 184 70.857s67.429 113.714 70.857 184c3.429 60.571 2.857 120.571 2.857 181.143z"></path>
                                    </svg>
                                </div>
                            </a>
                            <a
                                href="https://www.facebook.com/DanceLocaleHQ/"
                                target="_blank"
                                rel="noreferrer noopener"
                                className="home-link6">
                                <div className="home-container27">
                                    <svg viewBox="0 0 24 24" className="home-icon27">
                                        <path d="M12 2.04C6.5 2.04 2 6.53 2 12.06C2 17.06 5.66 21.21 10.44 21.96V14.96H7.9V12.06H10.44V9.85C10.44 7.34 11.93 5.96 14.22 5.96C15.31 5.96 16.45 6.15 16.45 6.15V8.62H15.19C13.95 8.62 13.56 9.39 13.56 10.18V12.06H16.34L15.89 14.96H13.56V21.96A10 10 0 0 0 22 12.06C22 6.53 17.5 2.04 12 2.04Z"></path>
                                    </svg>
                                </div>
                            </a>
                            <a
                                href="https://x.com"
                                target="_blank"
                                rel="noreferrer noopener"
                                className="home-link7">
                                <div className="home-container28">
                                    <svg viewBox="0 0 950.8571428571428 1024" className="home-icon29">
                                        <path d="M925.714 233.143c-25.143 36.571-56.571 69.143-92.571 95.429 0.571 8 0.571 16 0.571 24 0 244-185.714 525.143-525.143 525.143-104.571 0-201.714-30.286-283.429-82.857 14.857 1.714 29.143 2.286 44.571 2.286 86.286 0 165.714-29.143 229.143-78.857-81.143-1.714-149.143-54.857-172.571-128 11.429 1.714 22.857 2.857 34.857 2.857 16.571 0 33.143-2.286 48.571-6.286-84.571-17.143-148-91.429-148-181.143v-2.286c24.571 13.714 53.143 22.286 83.429 23.429-49.714-33.143-82.286-89.714-82.286-153.714 0-34.286 9.143-65.714 25.143-93.143 90.857 112 227.429 185.143 380.571 193.143-2.857-13.714-4.571-28-4.571-42.286 0-101.714 82.286-184.571 184.571-184.571 53.143 0 101.143 22.286 134.857 58.286 41.714-8 81.714-23.429 117.143-44.571-13.714 42.857-42.857 78.857-81.143 101.714 37.143-4 73.143-14.286 106.286-28.571z"></path>
                                    </svg>
                                </div>
                            </a>
                            <a
                                href="http://www.youtube.com/@DanceLocale"
                                target="_blank"
                                rel="noreferrer noopener"
                                className="home-link8">
                                <div className="home-container29">
                                    <svg viewBox="0 0 1024 1024" className="home-icon31">
                                        <path d="M406.286 644.571l276.571-142.857-276.571-144.571v287.429zM512 152c215.429 0 358.286 10.286 358.286 10.286 20 2.286 64 2.286 102.857 43.429 0 0 31.429 30.857 40.571 101.714 10.857 82.857 10.286 165.714 10.286 165.714v77.714s0.571 82.857-10.286 165.714c-9.143 70.286-40.571 101.714-40.571 101.714-38.857 40.571-82.857 40.571-102.857 42.857 0 0-142.857 10.857-358.286 10.857v0c-266.286-2.286-348-10.286-348-10.286-22.857-4-74.286-2.857-113.143-43.429 0 0-31.429-31.429-40.571-101.714-10.857-82.857-10.286-165.714-10.286-165.714v-77.714s-0.571-82.857 10.286-165.714c9.143-70.857 40.571-101.714 40.571-101.714 38.857-41.143 82.857-41.143 102.857-43.429 0 0 142.857-10.286 358.286-10.286v0z"></path>
                                    </svg>
                                </div>
                            </a>
                        </div>
                    </div>
                </footer>
            </div>
        </div>
    );
};

export default HowItWorks;
